import { Graphics, BlurFilter } from "pixi.js";
import type { Application, Container } from "pixi.js";

/**
 * Tipos de transición de escena. "cut" = swap instantáneo (sin animación).
 * El resto anima los dos Roots vivos (saliente + entrante) durante la ventana:
 *  - fade / fade-black / zoom / zoom-out  → alpha (+ scale)
 *  - slide   → ambas escenas se mueven juntas
 *  - cover   → la entrante se desliza ENCIMA de la saliente quieta
 *  - reveal  → la saliente se corre y DESCUBRE la entrante quieta debajo
 *  - wipe    → barrido con borde duro (mask rectangular animada)
 *  - iris    → máscara circular que abre desde el centro
 *  - blur     → crossfade con desenfoque (saliente desenfoca, entrante enfoca)
 */
export type SceneTransitionType =
  | "cut"
  | "fade"
  | "fade-black"
  | "slide-left"
  | "slide-right"
  | "slide-up"
  | "slide-down"
  | "cover-left"
  | "cover-right"
  | "cover-up"
  | "cover-down"
  | "reveal-left"
  | "reveal-right"
  | "reveal-up"
  | "reveal-down"
  | "zoom"
  | "zoom-out"
  | "wipe-left"
  | "wipe-right"
  | "wipe-up"
  | "wipe-down"
  | "iris"
  | "blur"
  | "stinger";

/** Config del asset de un stinger (video WebM-alpha en el workspace del usuario). */
export interface StingerConfig {
  /** Directorio workspace (con slash final), p.ej. "stingers/". */
  directory: string;
  fileName: string;
  /** ms (absoluto en el video) donde ocurre el swap. Derivado = medio de [start,end]. */
  cutPointMs: number;
  /** Inicio del tramo que se reproduce (ms absoluto en el video). Default 0. */
  startMs?: number;
  /** Fin del tramo que se reproduce (ms absoluto). Ahí se quita el overlay. Default = fin del video. */
  endMs?: number;
  /** Reproducir el audio del stinger (ruteado al mix del broadcast). Default off. */
  sound?: boolean;
}

/** Todos los tipos, para poblar el selector de UI. */
export const SCENE_TRANSITION_TYPES: SceneTransitionType[] = [
  "cut",
  "fade",
  "fade-black",
  "slide-left",
  "slide-right",
  "slide-up",
  "slide-down",
  "cover-left",
  "cover-right",
  "cover-up",
  "cover-down",
  "reveal-left",
  "reveal-right",
  "reveal-up",
  "reveal-down",
  "zoom",
  "zoom-out",
  "wipe-left",
  "wipe-right",
  "wipe-up",
  "wipe-down",
  "iris",
  "blur",
  "stinger",
];

export interface SceneTransition {
  type: SceneTransitionType;

  /** Duración de la animación en ms (ignorada para "cut" y "stinger"). */
  durationMs: number;

  /** Sólo para type === "stinger": el asset + cut-point. */
  stinger?: StingerConfig;
}

export const DEFAULT_SCENE_TRANSITION: SceneTransition = {
  type: "fade",
  durationMs: 350,
};

// Clave de localStorage donde `sceneTransitionAtom` (atomWithStorage) persiste el
// default global. PixiFactory lo lee de acá (no del store de jotai) porque la app
// usa un store propio vía <Provider store> y los servicios corren con otro store.
export const SCENE_TRANSITION_STORAGE_KEY = "eriol.sceneTransition";

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

/**
 * Anima la transición entre dos Roots de escena. Aprovecha que el SceneCompositor
 * mantiene ambas escenas vivas: la transición es un tween sobre los dos containers
 * (alpha / position / scale / mask), no un rebuild. Corre sobre App.ticker.
 *
 * Contrato: al entrar, ambos Roots ya están visibles (renderable). Al terminar,
 * resetea las transformaciones a identidad y dispara onDone() (que el compositor
 * usa para ocultar+pausar el saliente).
 */
export default class TransitionEngine {
  private App: Application;
  private Width: number;
  private Height: number;
  // Completa la transición en curso al instante (snap-to-end + onDone + resolve).
  // Necesario para que un switch que interrumpe a otro no deje el layer saliente
  // huérfano a medio fundir ni la Promise del switch anterior colgada.
  private finishCurrent: (() => void) | null = null;
  // Graphics de máscara para wipe/iris (vive en el toRoot durante la transición).
  private maskGfx: Graphics | null = null;
  // BlurFilters para "blur" (uno por Root). Se quitan/destruyen en finish.
  private blurFrom: BlurFilter | null = null;
  private blurTo: BlurFilter | null = null;

  constructor(app: Application, width: number, height: number) {
    this.App = app;
    this.Width = width;
    this.Height = height;
  }

  /** Resetea un Root a identidad (post-transición o cancelación). */
  private reset(root: Container): void {
    if (root.destroyed) return;
    root.alpha = 1;
    root.position.set(0, 0);
    root.scale.set(1, 1);
    root.pivot.set(0, 0);
    root.zIndex = 0;
  }

  /** Quita y destruye la máscara (wipe/iris) del toRoot. */
  private clearMask(toRoot: Container): void {
    if (!this.maskGfx) return;
    if (!toRoot.destroyed) toRoot.mask = null;
    this.maskGfx.removeFromParent();
    this.maskGfx.destroy();
    this.maskGfx = null;
  }

  /** Quita y destruye los BlurFilters (blur) de ambos Roots. */
  private clearBlur(fromRoot: Container, toRoot: Container): void {
    if (this.blurFrom) {
      if (!fromRoot.destroyed) fromRoot.filters = [];
      this.blurFrom.destroy();
      this.blurFrom = null;
    }
    if (this.blurTo) {
      if (!toRoot.destroyed) toRoot.filters = [];
      this.blurTo.destroy();
      this.blurTo = null;
    }
  }

  /**
   * Corta la transición en curso COMPLETÁNDOLA al instante: snap al estado final,
   * oculta+pausa su saliente (su onDone) y resuelve su Promise. Si no, interrumpir
   * una transición dejaría el saliente pegado a medio fundir y el switch anterior
   * colgado para siempre (su await nunca resolvería).
   */
  cancel(): void {
    if (this.finishCurrent) this.finishCurrent();
  }

  run(
    fromRoot: Container,
    toRoot: Container,
    transition: SceneTransition,
    onDone: () => void,
  ): Promise<void> {
    // Completa instantáneamente cualquier transición en curso antes de la nueva.
    this.cancel();

    if (transition.type === "cut") {
      this.reset(fromRoot);
      this.reset(toRoot);
      onDone();
      return Promise.resolve();
    }

    const type = transition.type;
    const dur = Math.max(16, transition.durationMs);
    const W = this.Width;
    const H = this.Height;
    const cx = W / 2;
    const cy = H / 2;
    const maxR = Math.hypot(W / 2, H / 2);

    const isReveal = type.startsWith("reveal");
    const usesMask = type.startsWith("wipe") || type === "iris";
    // Strength máximo del blur, proporcional a la resolución (≈12px@1080, 24@4K)
    // para que el desenfoque se vea igual en editor y OSR.
    const maxBlur = W / 160;

    // Reset + orden de dibujo DETERMINISTA. Por defecto el ENTRANTE va arriba; en
    // "reveal" el SALIENTE va arriba (se corre y descubre al entrante debajo).
    this.reset(fromRoot);
    this.reset(toRoot);
    if (isReveal) {
      fromRoot.zIndex = 1;
      toRoot.zIndex = 0;
    } else {
      fromRoot.zIndex = 0;
      toRoot.zIndex = 1;
    }

    // Estado inicial del entrante según el tipo.
    switch (type) {
      case "fade":
      case "fade-black":
        toRoot.alpha = 0;
        break;
      case "slide-left":
      case "cover-left":
        toRoot.position.set(W, 0);
        break;
      case "slide-right":
      case "cover-right":
        toRoot.position.set(-W, 0);
        break;
      case "slide-up":
      case "cover-up":
        toRoot.position.set(0, H);
        break;
      case "slide-down":
      case "cover-down":
        toRoot.position.set(0, -H);
        break;
      case "zoom":
        toRoot.pivot.set(cx, cy);
        toRoot.position.set(cx, cy);
        toRoot.scale.set(0.85, 0.85);
        toRoot.alpha = 0;
        break;
      case "zoom-out":
        toRoot.pivot.set(cx, cy);
        toRoot.position.set(cx, cy);
        toRoot.scale.set(1.15, 1.15);
        toRoot.alpha = 0;
        break;
      case "blur":
        toRoot.alpha = 0;
        break;
      default:
        break; // reveal-*: entrante estático debajo · wipe/iris: máscara abajo
    }

    // wipe/iris: máscara en el toRoot (que queda en identidad → coords = lienzo).
    if (usesMask) {
      this.maskGfx = new Graphics();
      this.maskGfx.label = "transition-mask";
      toRoot.addChild(this.maskGfx);
      toRoot.mask = this.maskGfx;
    }

    // blur: un BlurFilter por Root (saliente enfocado→borroso, entrante al revés).
    // quality baja (2 pasadas) para acotar el costo GPU; sólo dura la ventana.
    if (type === "blur") {
      // resolution 0.5: el blur es baja frecuencia → renderizarlo a media res es
      // ~4× más barato sin pérdida visible (clave para no castigar el fps en 4K).
      this.blurFrom = new BlurFilter({
        strength: 0,
        quality: 2,
        resolution: 0.5,
      });
      this.blurTo = new BlurFilter({
        strength: maxBlur,
        quality: 2,
        resolution: 0.5,
      });
      fromRoot.filters = [this.blurFrom];
      toRoot.filters = [this.blurTo];
    }

    const startMs = performance.now();

    return new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return; // idempotente: tick-final y cancel() pueden coincidir
        done = true;
        this.App.ticker.remove(tick);
        this.finishCurrent = null;
        this.clearMask(toRoot);
        this.clearBlur(fromRoot, toRoot);
        this.reset(toRoot);
        this.reset(fromRoot);
        onDone();
        resolve();
      };

      const drawMask = (e: number) => {
        const g = this.maskGfx;
        if (!g || g.destroyed) return;
        g.clear();
        switch (type) {
          case "wipe-right":
            g.rect(0, 0, W * e, H).fill(0xffffff);
            break;
          case "wipe-left":
            g.rect(W * (1 - e), 0, W * e, H).fill(0xffffff);
            break;
          case "wipe-down":
            g.rect(0, 0, W, H * e).fill(0xffffff);
            break;
          case "wipe-up":
            g.rect(0, H * (1 - e), W, H * e).fill(0xffffff);
            break;
          case "iris":
            g.circle(cx, cy, maxR * e).fill(0xffffff);
            break;
          default:
            break;
        }
      };

      const tick = () => {
        const elapsed = performance.now() - startMs;
        const p = Math.min(1, elapsed / dur);
        const e = easeInOutCubic(p);

        switch (type) {
          case "fade":
            toRoot.alpha = e;
            fromRoot.alpha = 1 - e;
            break;
          case "fade-black":
            // Funde el saliente a negro (1ª mitad) y entra el nuevo (2ª mitad);
            // el negro global del stage hace de medio.
            if (e < 0.5) {
              fromRoot.alpha = 1 - e * 2;
              toRoot.alpha = 0;
            } else {
              fromRoot.alpha = 0;
              toRoot.alpha = (e - 0.5) * 2;
            }
            break;
          case "slide-left":
            toRoot.position.set(W * (1 - e), 0);
            fromRoot.position.set(-W * e, 0);
            break;
          case "slide-right":
            toRoot.position.set(-W * (1 - e), 0);
            fromRoot.position.set(W * e, 0);
            break;
          case "slide-up":
            toRoot.position.set(0, H * (1 - e));
            fromRoot.position.set(0, -H * e);
            break;
          case "slide-down":
            toRoot.position.set(0, -H * (1 - e));
            fromRoot.position.set(0, H * e);
            break;
          case "cover-left":
            toRoot.position.set(W * (1 - e), 0); // saliente quieta
            break;
          case "cover-right":
            toRoot.position.set(-W * (1 - e), 0);
            break;
          case "cover-up":
            toRoot.position.set(0, H * (1 - e));
            break;
          case "cover-down":
            toRoot.position.set(0, -H * (1 - e));
            break;
          case "reveal-left":
            fromRoot.position.set(-W * e, 0); // entrante quieto debajo
            break;
          case "reveal-right":
            fromRoot.position.set(W * e, 0);
            break;
          case "reveal-up":
            fromRoot.position.set(0, -H * e);
            break;
          case "reveal-down":
            fromRoot.position.set(0, H * e);
            break;
          case "zoom":
            toRoot.scale.set(0.85 + 0.15 * e, 0.85 + 0.15 * e);
            toRoot.alpha = e;
            fromRoot.alpha = 1 - e;
            break;
          case "zoom-out":
            toRoot.scale.set(1.15 - 0.15 * e, 1.15 - 0.15 * e);
            toRoot.alpha = e;
            fromRoot.alpha = 1 - e;
            break;
          case "wipe-left":
          case "wipe-right":
          case "wipe-up":
          case "wipe-down":
          case "iris":
            drawMask(e);
            break;
          case "blur":
            // Crossfade + el saliente se desenfoca y el entrante se enfoca.
            toRoot.alpha = e;
            fromRoot.alpha = 1 - e;
            if (this.blurFrom) this.blurFrom.strength = maxBlur * e;
            if (this.blurTo) this.blurTo.strength = maxBlur * (1 - e);
            break;
          default:
            break;
        }

        if (p >= 1) finish();
      };

      this.finishCurrent = finish;
      this.App.ticker.add(tick);
    });
  }
}
